import MapboxDraw, { DrawCustomMode, DrawCustomModeThis } from "@mapbox/mapbox-gl-draw";
import { FeatureCollection, LineString, MultiLineString, Position } from "geojson";

import readOnlySelect from "fond/draw/modes/base/readOnlySelect";
import { lineString } from "fond/turf";

import { buildEndpointIndex, EndpointIndex } from "./chainSelection";
import { getRoadLayerIds } from "./constants";
import {
  buildFeatureBBoxIndex,
  classifyFeaturesBySide,
  findConnectedCrossings,
  findParallelFeatures,
  RoadSelectionIndex,
  RoadSideClassification,
} from "./roadSelection";

const {
  lib: {
    CommonSelectors: { isShiftDown },
  },
} = MapboxDraw;

const HIGHLIGHT_LAYER_ID = "road-select-highlight";
const HIGHLIGHT_SOURCE_ID = "road-select-highlight-source";

const emptyFC: FeatureCollection = { type: "FeatureCollection", features: [] };

interface SelectRoadState {
  hoveredFeature: mapboxgl.MapboxGeoJSONFeature | null;
  hoveredLineKey: string | null;
  roadLayerIds: string[];
  featureIndex: RoadSelectionIndex;
  endpointIndex: EndpointIndex;
  currentSideData: RoadSideClassification | null;
  [key: string]: unknown;
}

/**
 * Squared distance from a point to a line segment (in projected pixel space).
 */
export function sqDistToSegment(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const lenSq = dx * dx + dy * dy;

  if (lenSq === 0) return (px - ax) ** 2 + (py - ay) ** 2;

  const t = Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / lenSq));
  const projX = ax + t * dx;
  const projY = ay + t * dy;
  return (px - projX) ** 2 + (py - projY) ** 2;
}

/**
 * Given a MultiLineString geometry and a screen point, find the index of the
 * closest individual LineString. Uses the map to project coordinates to pixels
 * for accurate screen-space distance.
 */
export function findClosestLineIndex(map: mapboxgl.Map, geometry: MultiLineString, point: mapboxgl.Point): number {
  let bestIndex = 0;
  let bestDist = Infinity;
  const px = point.x;
  const py = point.y;

  for (let i = 0; i < geometry.coordinates.length; i++) {
    const coords = geometry.coordinates[i];
    for (let j = 0; j < coords.length - 1; j++) {
      const a = map.project(coords[j] as [number, number]);
      const b = map.project(coords[j + 1] as [number, number]);
      const d = sqDistToSegment(px, py, a.x, a.y, b.x, b.y);
      if (d < bestDist) {
        bestDist = d;
        bestIndex = i;
      }
    }
  }

  return bestIndex;
}

/**
 * Extract the closest individual LineString coordinates from a road feature,
 * handling both LineString and MultiLineString geometries.
 */
export function extractRoadCoords(
  map: mapboxgl.Map,
  feature: mapboxgl.MapboxGeoJSONFeature,
  point: mapboxgl.Point
): { coords: Position[]; lineIndex: number } {
  if (feature.geometry.type === "MultiLineString") {
    const lineIndex = findClosestLineIndex(map, feature.geometry as MultiLineString, point);
    return { coords: (feature.geometry as MultiLineString).coordinates[lineIndex], lineIndex };
  }
  return { coords: (feature.geometry as LineString).coordinates, lineIndex: 0 };
}

/**
 * Build a simple key to detect whether we're still hovering the same
 * individual line within a (Multi)LineString feature.
 */
export function lineKey(featureId: string | number | undefined, layerId: string | undefined, lineIndex: number): string {
  return `${featureId}:${layerId}:${lineIndex}`;
}

const selectRoad = {
  ...readOnlySelect,

  onSetup(this: DrawCustomModeThis) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const parentState = (readOnlySelect.onSetup?.call(this as any, {}) ?? {}) as Record<string, unknown>;

    const roadLayerIds = getRoadLayerIds(this.map);

    // Add a dedicated GeoJSON source + layer for the highlight.
    if (!this.map.getSource(HIGHLIGHT_SOURCE_ID)) {
      this.map.addSource(HIGHLIGHT_SOURCE_ID, { type: "geojson", data: emptyFC });
    }
    if (!this.map.getLayer(HIGHLIGHT_LAYER_ID)) {
      this.map.addLayer({
        id: HIGHLIGHT_LAYER_ID,
        type: "line",
        source: HIGHLIGHT_SOURCE_ID,
        paint: {
          "line-color": "#00bfff",
          "line-width": 8,
          "line-opacity": 0.75,
        },
      });
    }

    // Build spatial indexes of draw features for road-click selection
    const allFeatures = this._ctx.api.getAll().features;
    const featureIndex = buildFeatureBBoxIndex(allFeatures);
    const endpointIndex = buildEndpointIndex(allFeatures);

    return {
      ...parentState,
      hoveredFeature: null,
      hoveredLineKey: null,
      roadLayerIds,
      featureIndex,
      endpointIndex,
      currentSideData: null,
    } as SelectRoadState;
  },

  onMouseMove(this: DrawCustomModeThis, state: SelectRoadState, event: MapboxDraw.MapMouseEvent) {
    if (state.roadLayerIds.length === 0) return true;

    // Query a small area around the cursor for a more forgiving hit target
    const { x, y } = event.point;
    const hitBox: [mapboxgl.PointLike, mapboxgl.PointLike] = [
      [x - 5, y - 5],
      [x + 5, y + 5],
    ];

    const features = this.map.queryRenderedFeatures(hitBox, { layers: state.roadLayerIds });
    const roadFeature = features[0] ?? null;

    if (!roadFeature) {
      if (state.hoveredLineKey !== null) {
        const source = this.map.getSource(HIGHLIGHT_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined;
        source?.setData(emptyFC);
        this.updateUIClasses({ mouse: "" });
        state.hoveredFeature = null;
        state.hoveredLineKey = null;
      }
      return true;
    }

    // Determine which individual LineString to highlight
    const { coords: highlightCoords, lineIndex } = extractRoadCoords(this.map, roadFeature, event.point);

    const key = lineKey(roadFeature.id, roadFeature.layer?.id, lineIndex);
    if (key === state.hoveredLineKey) return true;

    const source = this.map.getSource(HIGHLIGHT_SOURCE_ID) as mapboxgl.GeoJSONSource | undefined;
    if (!source) return true;

    const highlightGeometry: LineString = { type: "LineString", coordinates: highlightCoords };
    source.setData({ type: "Feature", geometry: highlightGeometry, properties: {} });
    this.updateUIClasses({ mouse: "pointer" });

    state.hoveredFeature = roadFeature;
    state.hoveredLineKey = key;
    return true; // skip draw render
  },

  onClick(this: DrawCustomModeThis, state: SelectRoadState, event: MapboxDraw.MapMouseEvent) {
    const store = this._ctx.store as unknown as { setDirty(): void; render(): void };

    if (!state.hoveredFeature) {
      // Click on empty space — clear selection
      this.clearSelectedFeatures();
      state.currentSideData = null;
      store.setDirty();
      store.render();
      return;
    }

    // Extract the specific road LineString under the cursor
    const { coords: roadCoords } = extractRoadCoords(this.map, state.hoveredFeature, event.point);
    const road = lineString(roadCoords);

    // Find draw features that run parallel to this road, then pick up connected crossings
    const parallelIds = findParallelFeatures(state.featureIndex, road);
    if (parallelIds.size === 0) return;

    const crossingIds = findConnectedCrossings(state.endpointIndex, parallelIds);
    const allIds = new Set([...parallelIds, ...crossingIds]);

    // Select the features (shift-click adds, regular click replaces)
    const ids = Array.from(allIds);

    if (isShiftDown(event)) {
      for (const id of ids) {
        if (!this.isSelected(id)) {
          this.select(id);
        }
      }

      // Classify using the existing orientation so side labels stay consistent
      const prev = state.currentSideData;
      const sideData = classifyFeaturesBySide(state.featureIndex, road, parallelIds, crossingIds, prev?.orientation);
      const merged: RoadSideClassification = prev
        ? {
            orientation: prev.orientation,
            sideA: prev.sideA,
            sideB: prev.sideB,
            sideAIds: new Set([...prev.sideAIds, ...sideData.sideAIds]),
            sideBIds: new Set([...prev.sideBIds, ...sideData.sideBIds]),
            crossingIds: new Set([...prev.crossingIds, ...sideData.crossingIds]),
          }
        : sideData;

      state.currentSideData = merged;
      this.map.fire("draw.road.sidedata", { sideData: merged });
    } else {
      const sideData = classifyFeaturesBySide(state.featureIndex, road, parallelIds, crossingIds);
      this.setSelected(ids[0]);
      for (let i = 1; i < ids.length; i++) {
        this.select(ids[i]);
      }
      state.currentSideData = sideData;
      this.map.fire("draw.road.sidedata", { sideData });
    }

    store.setDirty();
    store.render();
  },

  onStop(this: DrawCustomModeThis) {
    // Remove the highlight layer and source
    if (this.map.getLayer(HIGHLIGHT_LAYER_ID)) {
      this.map.removeLayer(HIGHLIGHT_LAYER_ID);
    }
    if (this.map.getSource(HIGHLIGHT_SOURCE_ID)) {
      this.map.removeSource(HIGHLIGHT_SOURCE_ID);
    }
  },
} as unknown as DrawCustomMode;

export default selectRoad;
